import { useState } from 'react';
import { formatCount } from '@/lib/content';
import type { Article, ArticleSection } from '@/types/content';

interface ArticleBodyProps {
  article: Article;
}

export default function ArticleBody({ article }: ArticleBodyProps) {
  const [liked, setLiked] = useState(false);
  const [copied, setCopied] = useState(false);

  const headings = article.sections
    .map((section: ArticleSection, idx: number) => ({ ...section, idx }))
    .filter((section) => section.type === 'h2');

  const share = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <article className="min-w-0 flex-1">
      {headings.length > 0 && (
        <nav className="mb-8 rounded-lg border border-background-200 bg-background-100/60 p-5" aria-label="文章目录">
          <h2 className="flex items-center gap-2 font-heading text-base text-foreground-950">
            <i className="ri-list-check-2 text-primary-600" aria-hidden="true"></i>
            本文看点
          </h2>
          <ol className="mt-3 flex flex-col gap-2">
            {headings.map((h, idx) => (
              <li key={h.idx}>
                <a
                  href={`#section-${h.idx}`}
                  className="flex items-center gap-2 text-sm text-foreground-700 transition-colors hover:text-primary-700"
                >
                  <span className="font-heading text-xs text-foreground-400">{String(idx + 1).padStart(2, '0')}</span>
                  {h.text}
                </a>
              </li>
            ))}
          </ol>
        </nav>
      )}

      <div className="flex flex-col gap-5">
        {article.sections.map((section: ArticleSection, idx: number) => {
          if (section.type === 'h2') {
            return (
              <h2
                key={idx}
                id={`section-${idx}`}
                className="scroll-mt-24 pt-2 font-heading text-xl text-foreground-950 md:text-2xl"
              >
                {section.text}
              </h2>
            );
          }
          if (section.type === 'quote') {
            return (
              <blockquote
                key={idx}
                className="rounded-lg border-l-4 border-accent-500 bg-accent-50 px-5 py-4 text-sm leading-relaxed text-foreground-800 md:text-base"
              >
                {section.text}
              </blockquote>
            );
          }
          return (
            <p key={idx} className="text-[15px] leading-loose text-foreground-700 md:text-base">
              {section.text}
            </p>
          );
        })}
      </div>

      <div className="mt-8 flex flex-wrap gap-2">
        {article.tags.map((tag: string) => (
          <span key={tag} className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600">
            #{tag}
          </span>
        ))}
      </div>

      <div className="mt-8 flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100/60 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 text-sm text-foreground-600">
          <i className="ri-shield-check-line text-primary-600" aria-hidden="true"></i>
          觉得离谱就给个赞，我们继续挖下一颗瓜
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setLiked((v) => !v)}
            className={`flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md px-4 py-2 text-sm font-bold transition-colors ${
              liked
                ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                : 'border border-background-300 bg-background-50 text-foreground-800 hover:border-primary-300'
            }`}
          >
            <i className={liked ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'} aria-hidden="true"></i>
            {formatCount(article.likes + (liked ? 1 : 0))}
          </button>
          <button
            type="button"
            onClick={share}
            className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-bold text-foreground-800 transition-colors hover:border-primary-300"
          >
            <i className={copied ? 'ri-check-line' : 'ri-link'} aria-hidden="true"></i>
            {copied ? '已复制' : '复制链接'}
          </button>
        </div>
      </div>
    </article>
  );
}